import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { branch, compose } from 'recompose';

import { Message, translator } from 'mage-i18n';
import { Row, Column, VerticalRhythm, withResponsive } from 'mage-components';
import { SpacerHorizontal, Strong } from '@klarna/bubble-ui';
import { HorizontalWrapper, defaultContainerPropTypes } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { logEvent } from '@/utils/analytics';
import { BackButton, CallToActionButton, ContainerTemplate } from '@/components';
import { tPrefix } from '@/components/DocumentUpload/constants';
import {
  getBusinessDocuments,
  getUserMerchantId,
  merchantRegion,
  getMerchantAwareTranslationKey,
} from '@/selectors/merchant';
import {
  getStakeholdersWithUploadedDocuments as getStakeholdersWithUploadedDocumentsDocumentUpload,
  getDocuments,
} from '@/selectors/documentUpload';
import {
  getDocumentRequirementsForBusiness,
  getStakeholdersWithUploadedDocuments,
} from '@/flow/configs/selectors';
import { isDocumentUploadWidget } from '@/selectors/widget';
import { getFeatureByKey } from '@/selectors/featuresConfig';
import { withChainedCapabilitiesBeforeNext } from '@/flow/common';
import { CAPS, FEATURES } from '@/constants';

import { CY_DOCUMENTS_CONTAINER_IDS } from './constants';
import { ANALYTICS_EVENTS } from './analyticsEvents';
import { BusinessDocuments } from './components/BusinessDocuments';
import { StakeholderDocuments } from './components/StakeholderDocuments';

export class Documents extends React.Component {
  constructor(props) {
    super(props);

    this.allSubmitted = this.allSubmitted.bind(this);
  }

  allSubmitted() {
    const { documents = [], businessDocumentRequirements, stakeholdersWithDocuments } = this.props;

    const allRequiredStakeholderDocsUploaded = () =>
      stakeholdersWithDocuments.every(stakeholder =>
        stakeholder.documentRequirements
          .filter(d => d.required)
          .every(reqDoc => stakeholder?.documents?.some(doc => reqDoc.attachmentType === doc.type)),
      );

    const allRequiredBusinessDocsUploaded = () =>
      businessDocumentRequirements
        .filter(d => d.required)
        .every(reqDoc => documents.some(doc => reqDoc.attachmentType === doc.type));

    return allRequiredStakeholderDocsUploaded() && allRequiredBusinessDocsUploaded();
  }

  render() {
    const {
      error,
      mid,
      t,
      getTranslationKey,
      onBack,
      onNext,
      hasBack,
      businessUploadedDocuments,
      isDesktop,
      isLoading,
      isDisabled = false,
      businessDocumentRequirements,
      stakeholdersWithDocuments,
    } = this.props;

    const strong = value => <Strong>{value}</Strong>;

    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title`)}
        subtitle={
          <Message path={getTranslationKey(`${tPrefix}.subheadline`)} params={{ strong }} />
        }
        error={error}
      >
        {businessDocumentRequirements.length > 0 && (
          <BusinessDocuments
            uploadedDocuments={businessUploadedDocuments}
            documentRequirements={businessDocumentRequirements}
            t={t}
          />
        )}
        <StakeholderDocuments
          isDesktop={isDesktop}
          t={t}
          stakeholdersWithDocuments={stakeholdersWithDocuments}
        />
        <VerticalRhythm spacing={{ mobile: 5, tablet: 5, desktop: 8 }} />
        <Row>
          <Column
            columns={{ mobile: 12, tablet: 12, desktop: 12 }}
            alignItems={isDesktop ? 'flex-end' : undefined}
          >
            <HorizontalWrapper display="flex" justifyContent="space-between">
              {hasBack && (
                <>
                  <BackButton
                    tabIndex={2}
                    onClick={onBack}
                    disabled={isDisabled}
                    {...generateCyElemId(CY_DOCUMENTS_CONTAINER_IDS.BTN_CHANGE_PRODUCTS, window)}
                  >
                    {t(`${tPrefix}.buttons.back`)}
                  </BackButton>
                  <SpacerHorizontal medium />
                </>
              )}
              <CallToActionButton
                tabIndex={3}
                onClick={() => {
                  logEvent(ANALYTICS_EVENTS.fillDocuments);
                  onNext({ payload: { params: { mid } } });
                }}
                loading={isLoading}
                disabled={isDisabled || !this.allSubmitted()}
                {...generateCyElemId(CY_DOCUMENTS_CONTAINER_IDS.BTN_SUBMIT, window)}
              >
                {t(`${tPrefix}.buttons.submit`)}
              </CallToActionButton>
            </HorizontalWrapper>
          </Column>
        </Row>
      </ContainerTemplate>
    );
  }
}

Documents.propTypes = {
  ...defaultContainerPropTypes,
  getTranslationKey: PropTypes.func.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  isDocumentUploadWidget: PropTypes.bool.isRequired,
  mid: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  businessDocumentRequirements: PropTypes.array,
  businessUploadedDocuments: PropTypes.array,
  documents: PropTypes.array,
  modalContent: PropTypes.array,
  stakeholdersWithDocuments: PropTypes.array,
};

export default compose(
  connect(state => ({
    businessUploadedDocuments: getBusinessDocuments(state),
    documents: getDocuments(state),
    mid: getUserMerchantId(state),
    region: merchantRegion(state),
    businessDocumentRequirements: getDocumentRequirementsForBusiness(state),
    stakeholdersWithDocuments: isDocumentUploadWidget(state)
      ? getStakeholdersWithUploadedDocumentsDocumentUpload(state)
      : getStakeholdersWithUploadedDocuments(state),
    t: translator(state),
    isDocumentUploadWidget: isDocumentUploadWidget(state),
    getTranslationKey: getMerchantAwareTranslationKey(state),
    sendDocumentUploadDataToUnderwriting: getFeatureByKey(
      FEATURES.SEND_DOCUMENT_UPLOAD_DATA_TO_UNDERWRITING,
    )(state),
  })),
  branch(
    ({ sendDocumentUploadDataToUnderwriting, isDocumentUploadWidget }) =>
      sendDocumentUploadDataToUnderwriting && isDocumentUploadWidget,
    withChainedCapabilitiesBeforeNext(
      {
        completeUpload: CAPS.V2_DOC_UPLOAD_COMPLETE,
        completeToken: CAPS.V2_DOC_TOKEN_COMPLETE,
      },
      {
        forceCapabilitiesOrder: ['completeUpload', 'completeToken'],
      },
    ),
  ),
  branch(
    ({ sendDocumentUploadDataToUnderwriting, isDocumentUploadWidget }) =>
      isDocumentUploadWidget && !sendDocumentUploadDataToUnderwriting,
    withChainedCapabilitiesBeforeNext({
      completeToken: CAPS.V2_DOC_TOKEN_COMPLETE,
    }),
  ),
  branch(
    ({ sendDocumentUploadDataToUnderwriting, isDocumentUploadWidget }) =>
      !isDocumentUploadWidget && sendDocumentUploadDataToUnderwriting,
    withChainedCapabilitiesBeforeNext({
      completeUpload: CAPS.V2_DOC_UPLOAD_COMPLETE,
    }),
  ),
)(withResponsive(Documents));
